import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorFallbackProps {
  error: Error;
  retry: () => void;
}

/** Detect dynamic import / chunk loading failures (stale deployment). */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && msg.includes('Failed to fetch')) ||
    // Chunk served as HTML via SPA fallback (Vercel rewrite returns index.html for missing assets)
    (error.name === 'SyntaxError' && msg.includes("Unexpected token '<'"))
  );
}

function DefaultErrorFallback({ error, retry }: ErrorFallbackProps) {
  const chunkError = isChunkLoadError(error);

  return (
  <div
    role="alert"
    className="error-boundary"
    style={{
      padding: '20px',
      border: '1px solid #f5c6cb',
      borderRadius: '4px',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      margin: '20px 0'
    }}
  >
    <h2>{chunkError ? 'App updated — reload needed' : 'Something went wrong'}</h2>
    {chunkError ? (
      <p style={{ marginBottom: '20px' }}>
        A new version of the app was deployed. Please reload to continue.
      </p>
    ) : (
      <details style={{ whiteSpace: 'pre-wrap', marginBottom: '20px' }}>
        <summary>Error details</summary>
        {error.message}
        {process.env.NODE_ENV === 'development' && (
          <pre style={{ marginTop: '10px', fontSize: '12px' }}>
            {error.stack}
          </pre>
        )}
      </details>
    )}
    <button
      onClick={chunkError ? () => window.location.reload() : retry}
      style={{
        padding: '8px 16px',
        backgroundColor: '#721c24',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      {chunkError ? 'Reload page' : 'Try again'}
    </button>
  </div>
  );
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-reload once on chunk load failures (stale deployment)
    if (isChunkLoadError(error)) {
      const reloadKey = 'chunk-error-reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      if (!lastReload || Date.now() - Number(lastReload) > 10_000) {
        sessionStorage.setItem(reloadKey, String(Date.now()));
        window.location.reload();
        return;
      }
      // Already reloaded recently — fall through to show error UI
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
      console.error('Production error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }

    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  retry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} retry={this.retry} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;